import React, { Fragment, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useSidebar } from "../../../contexts/SidebarContext";
import useEvent from "../../../hooks/useEvent";
import {
  resetNoData,
  startAdHocSearch,
  stopAdHocSearch,
  clearAdHocResults,
} from "../../../actions/restaurants";
import { LIST_VIEW } from "../../../actions/types";
import Restaurant from "./Restaurant";
import { useAdHocSearch } from "../../../hooks/useAdHocSearch";
import FollowCityModal from "../FollowCityModal";
import SaveSearchModal from "../SaveSearchModal";
import { reverseGeocode } from "../../../util/geoUtils";

const EXPLORE_REJECTION_KEY = "vegifyit_explore_rejection";
const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Check if user has rejected explore prompt within the last 24 hours
const hasRecentRejection = () => {
  try {
    const rejectionTime = localStorage.getItem(EXPLORE_REJECTION_KEY);
    if (!rejectionTime) return false;

    const now = Date.now();
    const rejectionTimestamp = parseInt(rejectionTime, 10);
    const timeSinceRejection = now - rejectionTimestamp;

    // If rejection was less than 24 hours ago, don't show prompt
    return timeSinceRejection < ONE_DAY_MS;
  } catch (error) {
    // If localStorage is unavailable, allow prompt
    return false;
  }
};

// Store rejection timestamp in localStorage
const storeRejection = () => {
  try {
    localStorage.setItem(EXPLORE_REJECTION_KEY, Date.now().toString());
  } catch (error) {
    // If localStorage is unavailable, silently fail
    console.warn("Could not store explore rejection preference:", error);
  }
};

const ResultSideBody = ({
  shouldShow,
  noData,
  resetNoData,
  token,
  view,
  adHocSearchActive,
  adHocSearchJobId,
  adHocSearchLocation,
  adHocRestaurantsProcessed,
  adHocVeganResultsFound,
  adHocPromptForMore,
  mapCenter,
  currentLocation,
  startAdHocSearchAction,
  stopAdHocSearchAction,
  clearAdHocResultsAction,
  selectedRestaurantId,
}) => {
  const navigate = useNavigate();
  const { isSidebarOpen: sidebarOpen, setIsSidebarOpen: setSidebarOpen } =
    useSidebar();
  const { event } = useEvent();
  const useFollowFlow = !!event || !token;
  const [exploreRejected, setExploreRejected] = useState(false);
  const [showNoDataModal, setShowNoDataModal] = useState(false);
  const [showAlreadySearchingModal, setShowAlreadySearchingModal] =
    useState(false);
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [followCityInfo, setFollowCityInfo] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveModalInfo, setSaveModalInfo] = useState(null);

  const handleSaveSearchClick = async () => {
    if (!mapCenter?.lat || !mapCenter?.lng) return;
    const geo = await reverseGeocode(mapCenter.lat, mapCenter.lng);
    const urlParams = new URLSearchParams(window.location.search);
    const cuisineParam = urlParams.get("cuisine");
    setSaveModalInfo({
      cityName: geo.city || "this area",
      state: geo.state || "",
      lat: mapCenter.lat,
      lng: mapCenter.lng,
      keywords: urlParams.get("keywords") || "",
      cuisine: cuisineParam ? cuisineParam.split(",").filter(Boolean) : [],
      radius: currentLocation.radius,
    });
    setShowSaveModal(true);
  };

  const handleFollowCityClick = async () => {
    if (!mapCenter?.lat || !mapCenter?.lng) return;
    const geo = await reverseGeocode(mapCenter.lat, mapCenter.lng);
    setFollowCityInfo({
      cityName: geo.city || "this area",
      state: geo.state || "",
      lat: mapCenter.lat,
      lng: mapCenter.lng,
    });
    setShowFollowModal(true);
  };

  const handleFollowModalClose = () => {
    setShowFollowModal(false);
  };

  // Auto-open sidebar when view changes to LIST_VIEW or when a restaurant is selected (from map marker click)
  useEffect(function autoOpenSidebarOnListViewOrSelection() {
    if (view === LIST_VIEW || selectedRestaurantId) {
      // Check if we're in collapsible mode
      const width = window.innerWidth;
      if (width >= 600 && width < 1100) {
        setSidebarOpen(true);
      }
    }
  }, [view, selectedRestaurantId, setSidebarOpen]);

  // Handle sidebar toggle
  const handleToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Check if there are ad hoc restaurants in the results
  const hasAdHocResults = shouldShow?.some(
    (r) => r.metadata?.adHocSearch === true
  );

  // Ad hoc search connection is now handled by AdHocSearchConnection component in App.js
  // This ensures WebSocket stays connected even when navigating away

  // Handle prompt for more batches
  useEffect(function promptForMoreAdHocBatches() {
    if (adHocPromptForMore) {
      const shouldContinue = window.confirm(
        "No vegan options found in first 20 restaurants. Continue searching?"
      );
      if (shouldContinue) {
        // TODO: Implement continue search logic
        console.log("User chose to continue searching");
      } else {
        // TODO: Implement stop search logic
        console.log("User chose to stop searching");
      }
    }
  }, [adHocPromptForMore]);

  // Handle ad hoc search
  const handleAdHocSearch = async () => {
    try {
      const lat = mapCenter.lat;
      const lng = mapCenter.lng;
      const radius = currentLocation.radius;

      // Check if search already in progress for this location
      if (
        adHocSearchLocation &&
        adHocSearchLocation.lat === lat &&
        adHocSearchLocation.lng === lng &&
        adHocSearchLocation.radius === radius &&
        adHocSearchJobId
      ) {
        setShowAlreadySearchingModal(true);
        return;
      }

      // Start ad hoc search
      await startAdHocSearchAction(lat, lng, radius);
    } catch (error) {
      console.error("Error starting ad hoc search:", error);
      alert(error.response?.data?.error || "Failed to start discovery search");
    }
  };

  // Handle cancel ad hoc search
  const handleCancelAdHocSearch = async () => {
    try {
      if (adHocSearchJobId) {
        await stopAdHocSearchAction(adHocSearchJobId);
      } else {
        // Fallback if no jobId
        stopAdHocSearchAction();
      }
    } catch (error) {
      console.error("Error cancelling ad hoc search:", error);
      alert(error.response?.data?.error || "Failed to cancel discovery search");
    }
  };

  // Handle clear ad hoc results
  const handleClearAdHocResults = () => {
    clearAdHocResultsAction();
  };

  // Check localStorage on mount to see if user recently rejected
  useEffect(function checkExploreRejectionOnMount() {
    if (hasRecentRejection()) {
      setExploreRejected(true);
    }
  }, []);

  // Show modal instead of window.confirm
  useEffect(function showNoDataModalWhenEmpty() {
    if (noData === true && !exploreRejected && !showNoDataModal) {
      setShowNoDataModal(true);
    } else if (noData === true && exploreRejected) {
      // If user has rejected the explore prompt, reset noData so empty state can show
      resetNoData();
    } else if (noData === false && showNoDataModal) {
      // Results arrived after modal was shown (race condition) — dismiss it
      setShowNoDataModal(false);
    }
  }, [noData, exploreRejected, showNoDataModal, resetNoData]);

  const handleExploreClick = () => {
    setShowNoDataModal(false);
    setExploreRejected(true);
    navigate("/explore");
  };

  const handleDismissModal = () => {
    setShowNoDataModal(false);
    setExploreRejected(true);
    storeRejection(); // Persist the rejection for 24 hours
  };

  // Determine if we're in collapsible mode (medium width)
  const [isCollapsibleMode, setIsCollapsibleMode] = useState(false);

  useEffect(function setupCollapsibleModeListener() {
    const checkCollapsibleMode = () => {
      const width = window.innerWidth;
      // Medium breakpoint: 600px - 1099px
      setIsCollapsibleMode(width >= 600 && width < 1100);
    };

    checkCollapsibleMode();
    window.addEventListener("resize", checkCollapsibleMode);
    return () => window.removeEventListener("resize", checkCollapsibleMode);
  }, []);

  return (
    <div
      className={`result-sidebody-holder ${
        view === LIST_VIEW ? "open" : "closed"
      } ${isCollapsibleMode ? "collapsible-mode" : ""} ${
        isCollapsibleMode && sidebarOpen ? "sidebar-open" : "sidebar-closed"
      }`}
    >
      {isCollapsibleMode && (
        <button
          className="sidebar-toggle-button"
          onClick={handleToggle}
          aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
          aria-expanded={sidebarOpen}
        >
          <i className={`fas ${sidebarOpen ? "fa-times" : "fa-bars"}`}></i>
        </button>
      )}
      <div
        className={`result-sidebody
              ${view === LIST_VIEW ? "open" : "closed"} ${
          isCollapsibleMode && sidebarOpen ? "sidebar-open" : ""
        }`}
      >
        {shouldShow && shouldShow.length > 0 && (
          <Fragment>
            <div className="result result-header">
              <span>Restaurants:</span>
              <button
                type="button"
                className="follow-city-link"
                onClick={useFollowFlow ? handleFollowCityClick : handleSaveSearchClick}
              >
                <i className={`fas ${useFollowFlow ? "fa-bell" : "fa-bookmark"}`}></i>
                <span>{useFollowFlow ? "Follow" : "Save"}</span>
              </button>
            </div>
            <Restaurant isOpen={true} />
          </Fragment>
        )}
        {(!shouldShow || shouldShow.length === 0) &&
          (!noData || exploreRejected) && (
            <div className="empty-state">
              <i className="fas fa-utensils empty-state-icon"></i>
              <h3 className="empty-state-title">No restaurants found</h3>
              {event ? (
                <Fragment>
                  <p className="empty-state-message">
                    No results found for this event. You're currently searching
                    within the event area only.
                  </p>
                  {mapCenter?.lat && mapCenter?.lng && (
                    <div
                      className="empty-state-cta"
                      style={{ marginTop: "20px" }}
                    >
                      <Link
                        to={`/?lat=${mapCenter.lat}&lng=${mapCenter.lng}`}
                        className="btn-primary"
                      >
                        Search All Restaurants Near You
                      </Link>
                    </div>
                  )}
                  <div className="empty-state-follow" style={{ marginTop: "20px" }}>
                    <p className="empty-state-follow-text">
                      Want to know when vegan options are added here?
                    </p>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleFollowCityClick}
                    >
                      <i
                        className="fas fa-bell"
                        style={{ marginRight: "8px" }}
                      ></i>
                      Follow This City
                    </button>
                  </div>
                </Fragment>
              ) : (
                <Fragment>
                  <p className="empty-state-message">
                    Try adjusting your search filters or location
                  </p>
                  <div className="empty-state-cta">
                    {token ? (
                      <Fragment>
                        <p className="empty-state-cta-text">
                          Don't see your favorite restaurant? Help grow the
                          community!
                        </p>
                        <div className="empty-state-buttons">
                          <Link to="/?create=true" className="btn-primary">
                            Add a Restaurant
                          </Link>
                          <button
                              type="button"
                              onClick={handleAdHocSearch}
                              className="btn-secondary ad-hoc-empty-button"
                              disabled={adHocSearchActive}
                              aria-label="Discovery Search"
                            >
                              <i
                                className="fas fa-search"
                                style={{ marginRight: "8px" }}
                              ></i>
                              ✨ Discovery Search
                            </button>
                        </div>
                      </Fragment>
                    ) : (
                      <Fragment>
                        <p className="empty-state-cta-text">
                          Don't see your favorite restaurant? Join us and add
                          it!
                        </p>
                        <Link to="/signup" className="btn-primary">
                          Sign Up & Add Restaurant
                        </Link>
                      </Fragment>
                    )}
                  </div>
                  <div className="empty-state-follow">
                    <p className="empty-state-follow-text">
                      Want to know when vegan options are added here?
                    </p>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={useFollowFlow ? handleFollowCityClick : handleSaveSearchClick}
                    >
                      <i
                        className="fas fa-bell"
                        style={{ marginRight: "8px" }}
                      ></i>
                      Follow This City
                    </button>
                  </div>
                </Fragment>
              )}
            </div>
          )}
        {/* Ad Hoc Search Widget - replaces CTA footer - only show when there are results */}
        {token && shouldShow && shouldShow.length > 0 && (
          <div className="result-cta-footer">
            {!adHocSearchActive &&
            (hasAdHocResults ||
              (adHocRestaurantsProcessed > 0 &&
                (adHocSearchJobId || adHocSearchLocation))) ? (
              /* Complete State - show completion and buttons */
              <div className="result ad-hoc-result ad-hoc-result-complete">
                <div className="ad-hoc-result-content">
                  <i className="fas fa-check-circle ad-hoc-complete-icon-small"></i>
                  <span className="ad-hoc-result-text">
                    Discovery Search Complete
                  </span>
                  <div className="ad-hoc-buttons-group">
                    <button
                      type="button"
                      onClick={handleClearAdHocResults}
                      className="btn-secondary ad-hoc-button-inline"
                      aria-label="Clear Discovery Results"
                    >
                      <i
                        className="fas fa-eraser"
                        style={{ marginRight: "4px" }}
                      ></i>
                      Clear Results
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Default State - show CTA with inline ad hoc search */
              <Fragment>
                {token ? (
                  <div className="result-cta-text">
                    Don't see your favorite restaurant or restaurant dish?{" "}
                    <span className="ad-hoc-tooltip-container">
                      <button
                        type="button"
                        onClick={handleAdHocSearch}
                        className="ad-hoc-inline-link"
                        aria-label="Discovery Search"
                      >
                        Try discovery search
                      </button>
                      <span className="ad-hoc-tooltip">
                        Discovery search automatically finds restaurants in
                        your area and checks their menus for vegan options.
                        Great for finding hidden gems!
                      </span>
                    </span>{" "}
                    or <Link to="/?create=true">add it here!</Link>
                  </div>
                ) : (
                  <Fragment>
                    Don't see your favorite restaurant or restaurant dish?
                    Please <Link to="/signup">make an account</Link> and add it!
                  </Fragment>
                )}
              </Fragment>
            )}
          </div>
        )}
        {!token && shouldShow && shouldShow.length > 0 && (
          <div className="result-cta-footer">
            Don't see your favorite restaurant or restaurant dish? Please{" "}
            <Link to="/signup">make an account</Link> and add it!
          </div>
        )}
      </div>
      {showNoDataModal &&
        createPortal(
          <div className="modal-overlay" onClick={handleDismissModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button
                className="modal-close"
                onClick={handleDismissModal}
                aria-label="Close modal"
              >
                ×
              </button>
              <i className="fas fa-map-marked-alt modal-icon"></i>
              <h3 className="modal-title">
                No VegifyIt results in this area yet
              </h3>
              <p className="modal-message">
                Would you like to explore our supported areas?
              </p>
              <div className="modal-actions">
                <button
                  className="btn-primary"
                  onClick={useFollowFlow ? handleFollowCityClick : handleSaveSearchClick}
                >
                  <i
                    className="fas fa-bell"
                    style={{ marginRight: "6px" }}
                  ></i>
                  Follow This City
                </button>
                <button className="btn-primary" onClick={handleExploreClick}>
                  Explore Areas
                </button>
                <button className="btn-secondary" onClick={handleDismissModal}>
                  Maybe Later
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      {showAlreadySearchingModal &&
        createPortal(
          <div
            className="modal-overlay"
            onClick={() => setShowAlreadySearchingModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button
                className="modal-close"
                onClick={() => setShowAlreadySearchingModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
              <i
                className="fas fa-search modal-icon"
                style={{ color: "var(--color-primary)" }}
              ></i>
              <h3 className="modal-title">Discovery Search Already Running</h3>
              <p className="modal-message">
                A discovery search is already in progress for this location.
                Please wait for it to complete or cancel the current search
                before starting a new one.
              </p>
              <div className="modal-actions">
                <button
                  className="btn-primary"
                  onClick={() => setShowAlreadySearchingModal(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      {showFollowModal && followCityInfo && (
        <FollowCityModal
          cityName={followCityInfo.cityName}
          state={followCityInfo.state}
          lat={followCityInfo.lat}
          lng={followCityInfo.lng}
          onClose={handleFollowModalClose}
        />
      )}
      {showSaveModal && saveModalInfo && (
        <SaveSearchModal
          cityName={saveModalInfo.cityName}
          state={saveModalInfo.state}
          lat={saveModalInfo.lat}
          lng={saveModalInfo.lng}
          keywords={saveModalInfo.keywords}
          cuisine={saveModalInfo.cuisine}
          radius={saveModalInfo.radius}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  shouldShow: state.restaurant.restaurants,
  noData: state.restaurant.noData,
  token: state.auth.token,
  view: state.results.view,
  adHocSearchActive: state.restaurant.adHocSearchActive,
  adHocSearchJobId: state.restaurant.adHocSearchJobId,
  adHocSearchLocation: state.restaurant.adHocSearchLocation,
  adHocRestaurantsProcessed: state.restaurant.adHocRestaurantsProcessed,
  adHocVeganResultsFound: state.restaurant.adHocVeganResultsFound,
  adHocPromptForMore: state.restaurant.adHocPromptForMore,
  mapCenter: state.mapCenter,
  currentLocation: state.currentLocation,
  selectedRestaurantId: state.restaurant.selected,
});
export default connect(mapStateToProps, {
  resetNoData,
  startAdHocSearchAction: startAdHocSearch,
  stopAdHocSearchAction: stopAdHocSearch,
  clearAdHocResultsAction: clearAdHocResults,
})(ResultSideBody);
